import React, { useContext, useEffect, useRef } from 'react';
import { FormContext } from '../../context/EquipmentFormContext';
import { LuBox } from 'react-icons/lu';
import {
  FiUploadCloud,
  FiX,
  FiImage,
} from 'react-icons/fi';

const subCategoryOptions = {
  Refrigeration: [
    'Walk-in Fridge',
    'Walk-in Freezer',
    'Display Fridge',
    'Under Counter Fridge',
    'Chest Freezer',
    'Upright Freezer',
  ],

  'Cooking Equipment': [
    'Commercial Oven',
    'Combi Oven',
    'Grill',
    'Fryer',
    'Hob',
    'Microwave',
    'Cooking Range',
  ],

  Electrical: [
    'Electrical Appliance',
    'Distribution Board',
    'Power Supply',
    'Commercial Mixer',
    'Food Processor',
    'Dishwasher',
  ],

  'Cleaning Equipment': [
    'Dishwasher',
    'Pressure Washer',
    'Floor Scrubber',
    'Vacuum Cleaner',
    'Steam Cleaner',
    'Washing Machine',
  ],

  'Fire Safety': [
    'Fire Extinguisher',
    'Fire Alarm',
    'Fire Blanket',
    'Emergency Lighting',
    'Fire Door',
    'Sprinkler System',
  ],

  Other: [
    'Furniture',
    'Storage Equipment',
    'Security Equipment',
    'Ventilation Equipment',
    'Other',
  ],
};

export default function Step1() {
  const {
    formData,
    updateField,
  } = useContext(FormContext);

  const fileInputRef = useRef(null);

  const subCategories =
    subCategoryOptions[formData.category] || [];

  // ============================================================
  // CLEANUP OBJECT URL
  // ============================================================

  useEffect(() => {
    return () => {
      if (formData.photoUrl) {
        URL.revokeObjectURL(formData.photoUrl);
      }
    };
  }, [formData.photoUrl]);

  // ============================================================
  // CATEGORY CHANGE
  // ============================================================

  const handleCategoryChange = (e) => {
    const category = e.target.value;

    updateField('category', category);

    // Reset subcategory when category changes
    updateField('subCategory', '');
  };

  // ============================================================
  // PHOTO SELECT
  // ============================================================

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    // ==========================================================
    // VALIDATE FILE TYPE
    // ==========================================================

    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/webp',
    ];

    if (!allowedTypes.includes(file.type)) {
      alert(
        'Please select a JPG, PNG, or WEBP image.'
      );

      e.target.value = '';
      return;
    }

    // ==========================================================
    // VALIDATE FILE SIZE
    // Maximum 5 MB
    // ==========================================================

    const maxSize = 5 * 1024 * 1024;

    if (file.size > maxSize) {
      alert(
        'Image size must be less than 5 MB.'
      );

      e.target.value = '';
      return;
    }

    // ==========================================================
    // CREATE PREVIEW
    // ==========================================================

    const previewUrl =
      URL.createObjectURL(file);

    // ==========================================================
    // STORE BOTH FILE + PREVIEW URL IN CONTEXT
    // ==========================================================

    updateField(
      'photoFile',
      file
    );

    updateField(
      'photoUrl',
      previewUrl
    );
  };

  // ============================================================
  // OPEN FILE SELECTOR
  // ============================================================

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // ============================================================
  // REMOVE PHOTO
  // ============================================================

  const handleRemovePhoto = () => {
    if (formData.photoUrl) {
      URL.revokeObjectURL(formData.photoUrl);
    }

    updateField(
      'photoFile',
      null
    );

    updateField(
      'photoUrl',
      ''
    );

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">

      {/* ====================================================== */}
      {/* HEADER */}
      {/* ====================================================== */}

      <div className="flex items-start space-x-4 mb-6">

        <div className="p-2 bg-blue-600 rounded-lg text-white">
          <LuBox className="w-5 h-5 stroke-[2.5]" />
        </div>

        <div className="flex-1">

          <div className="flex items-center space-x-2">

            <span className="text-xs font-bold bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
              1
            </span>

            <h2 className="text-base font-bold text-indigo-950">
              Equipment Identity
            </h2>

          </div>

          <p className="text-xs text-slate-500 mt-0.5">
            Basic details to identify the equipment.
          </p>

        </div>

      </div>

      {/* ====================================================== */}
      {/* MAIN FORM */}
      {/* ====================================================== */}

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">

        {/* ==================================================== */}
        {/* LEFT SIDE */}
        {/* ==================================================== */}

        <div className="md:col-span-9 grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* ================================================== */}
          {/* EQUIPMENT NAME */}
          {/* ================================================== */}

          <div>

            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Equipment Name{' '}
              <span className="text-red-500">*</span>
            </label>

            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                updateField(
                  'name',
                  e.target.value
                )
              }
              placeholder="e.g. Walk-in Fridge"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />

          </div>

          {/* ================================================== */}
          {/* CATEGORY + SUBCATEGORY */}
          {/* ================================================== */}

          <div className="grid grid-cols-2 gap-3">

            {/* CATEGORY */}

            <div>

              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Category{' '}
                <span className="text-red-500">*</span>
              </label>

              <select
                value={formData.category}
                onChange={handleCategoryChange}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              >

                <option value="">
                  Select category
                </option>

                <option value="Refrigeration">
                  Refrigeration
                </option>

                <option value="Cooking Equipment">
                  Cooking Equipment
                </option>

                <option value="Electrical">
                  Electrical
                </option>

                <option value="Cleaning Equipment">
                  Cleaning Equipment
                </option>

                <option value="Fire Safety">
                  Fire Safety
                </option>

                <option value="Other">
                  Other
                </option>

              </select>

            </div>

            {/* SUBCATEGORY */}

            <div>

              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Sub Category
              </label>

              <select
                value={formData.subCategory}
                onChange={(e) =>
                  updateField(
                    'subCategory',
                    e.target.value
                  )
                }
                disabled={!formData.category}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-400"
              >

                <option value="">
                  {formData.category
                    ? 'Select sub category'
                    : 'Select category first'}
                </option>

                {subCategories.map(
                  (subCategory) => (
                    <option
                      key={subCategory}
                      value={subCategory}
                    >
                      {subCategory}
                    </option>
                  )
                )}

              </select>

            </div>

          </div>

          {/* ================================================== */}
          {/* BRAND */}
          {/* ================================================== */}

          <div>

            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Brand
            </label>

            <input
              type="text"
              value={formData.brand}
              onChange={(e) =>
                updateField(
                  'brand',
                  e.target.value
                )
              }
              placeholder="e.g. Samsung"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none"
            />

          </div>

          {/* ================================================== */}
          {/* MODEL + ASSET ID */}
          {/* ================================================== */}

          <div className="grid grid-cols-2 gap-3">

            {/* MODEL */}

            <div>

              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Model
              </label>

              <input
                type="text"
                value={formData.model}
                onChange={(e) =>
                  updateField(
                    'model',
                    e.target.value
                  )
                }
                placeholder="e.g. RS8000"
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none"
              />

            </div>

            {/* ASSET ID */}

            <div>

              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Asset ID / Reference{' '}
                <span className="text-red-500">
                  *
                </span>
              </label>

              <input
                type="text"
                value={formData.assetId}
                onChange={(e) =>
                  updateField(
                    'assetId',
                    e.target.value
                  )
                }
                placeholder="e.g. REF-001"
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none"
              />

            </div>

          </div>

        </div>

        {/* ==================================================== */}
        {/* PHOTO UPLOAD */}
        {/* ==================================================== */}

        <div className="md:col-span-3">

          {/* HIDDEN FILE INPUT */}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handlePhotoChange}
            className="hidden"
          />

          {/* ================================================== */}
          {/* PHOTO PREVIEW */}
          {/* ================================================== */}

          {formData.photoUrl ? (

            <div className="relative h-full min-h-[180px] border border-slate-200 rounded-xl overflow-hidden bg-slate-50">

              <img
                src={formData.photoUrl}
                alt="Equipment preview"
                className="w-full h-full min-h-[180px] object-cover"
              />

              {/* REMOVE BUTTON */}

              <button
                type="button"
                onClick={handleRemovePhoto}
                className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center bg-white/95 text-red-500 rounded-full shadow-md hover:bg-red-50 transition-colors"
                title="Remove photo"
              >

                <FiX className="w-4 h-4" />

              </button>

              {/* CHANGE PHOTO */}

              <button
                type="button"
                onClick={handleUploadClick}
                className="absolute bottom-2 left-2 right-2 bg-white/95 text-blue-600 text-xs font-semibold py-2 rounded-lg shadow-sm hover:bg-white transition-colors"
              >
                Change Photo
              </button>

            </div>

          ) : (

            /* ================================================= */
            /* UPLOAD AREA */
            /* ================================================= */

            <button
              type="button"
              onClick={handleUploadClick}
              className="w-full h-full min-h-[180px] flex flex-col items-center justify-center border border-dashed border-slate-200 bg-slate-50/50 rounded-xl p-4 text-center hover:bg-slate-50 transition-colors"
            >

              <FiUploadCloud className="w-8 h-8 text-slate-400 mb-2" />

              <span className="text-xs font-bold text-blue-600">
                Upload Photo
              </span>

              <span className="text-[10px] text-slate-400 mt-1">
                JPG, PNG or WEBP
              </span>

              <span className="text-[10px] text-slate-400 mt-0.5">
                Maximum 5 MB
              </span>

            </button>

          )}

        </div>

      </div>

      {/* ====================================================== */}
      {/* SERIAL / BARCODE / PURCHASE DATE */}
      {/* ====================================================== */}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">

        {/* SERIAL NUMBER */}

        <div>

          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Serial Number
          </label>

          <input
            type="text"
            value={formData.serialNumber}
            onChange={(e) =>
              updateField(
                'serialNumber',
                e.target.value
              )
            }
            placeholder="Enter serial number"
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none"
          />

        </div>

        {/* BARCODE */}

        <div>

          <label className="block text-xs font-semibold text-slate-700 mb-1">

            Barcode / QR Code

            <span className="text-slate-400 font-normal">
              {' '}
              (optional)
            </span>

          </label>

          <input
            type="text"
            value={formData.barcode}
            onChange={(e) =>
              updateField(
                'barcode',
                e.target.value
              )
            }
            placeholder="Enter barcode / QR code"
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none"
          />

        </div>

        {/* PURCHASE DATE */}

        <div>

          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Purchase Date
          </label>

          <input
            type="date"
            value={formData.purchaseDate}
            onChange={(e) =>
              updateField(
                'purchaseDate',
                e.target.value
              )
            }
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          />

        </div>

      </div>

    </div>
  );
}